import { FC, useEffect, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Link, Stack } from '@chakra-ui/react';

import { InvoiceType } from '__generated__/GQL';
import Callout from 'components/_core/Callout';
import * as Form from 'components/_core/form';
import Text from 'components/_core/Text';
import { useModal } from 'components/ModalProvider/hooks';
import { logEvent } from 'features/Analytics/analytics';
import { InvoiceFormSchema } from 'features/Work/form/invoiceForm.schema';
import { QuoteFormSchema } from 'features/Work/form/quoteForm.schema';
import { ClientModalProvider } from 'features/Work/pages/Clients/components/Modals';
import { ClientModalType } from 'features/Work/pages/Clients/components/Modals/types';
import { FormClient } from 'features/Work/types';
import { datetimeToLocalDateUtc } from 'helpers/date';

import {
  INVOICE_NUMBER_ARTICLE,
  QUOTE_NUMBER_ARTICLE,
} from '../../../constants';
import locales from './locales';
import LogoUploadInput from './LogoUploadInput';

// Safety check year to prevent timestamps from being unprocessable
const MAX_YEAR = 2100;

interface GeneralContentProps {
  type: InvoiceType;
  clients: FormClient[];
  companyLogoUrl?: string | null;
}

const getClientsAsOptions: (
  clients: FormClient[],
) => { label: string; value: string }[] = (clients) =>
  clients.map((client) => ({
    label: client.name,
    value: client.invoiceClientId,
  }));

const GeneralContent: FC<GeneralContentProps> = ({
  clients,
  companyLogoUrl,
  type,
}) => {
  const { showModal } = useModal();
  const numberOfClients = useRef(clients.length);

  const itemLocales = locales[type];
  const clientOptions = getClientsAsOptions(clients);

  const { control, setValue } = useFormContext<
    InvoiceFormSchema | QuoteFormSchema
  >();

  // On client add, set the new client as selected
  useEffect(() => {
    if (numberOfClients.current === clients.length) {
      return;
    }

    numberOfClients.current = clients.length;

    // @ts-expect-error Object is possibly 'undefined'.
    setValue('clientId', clients[0].invoiceClientId ?? '');
  }, [clients, clients.length, setValue]);

  return (
    <Stack spacing="space-16">
      <ClientModalProvider>
        <LogoUploadInput logoUrl={companyLogoUrl ?? null} />

        {/* clientId */}
        <Controller
          control={control}
          name="clientId"
          render={({ field, fieldState }) => (
            <Form.Field
              error={fieldState.error?.message}
              label={locales.clientName}
            >
              <Form.SunshineSelect
                isInvalid={Boolean(fieldState.error?.message)}
                isSearchable
                onChange={(client) => {
                  if (!client) {
                    return;
                  }

                  field.onChange(client.value);
                }}
                options={[
                  Form.SunshineSelect.addOption({
                    buttonProps: {
                      onClick: () => {
                        logEvent({
                          name: 'Add Client',
                          properties: { from: 'ItemFormPage' },
                        });

                        showModal(ClientModalType.CreateEdit);
                      },
                    },
                    label: locales.addClient,
                    value: 'new-client',
                  }),

                  ...clientOptions,
                ]}
                value={clientOptions.find(
                  (client) => client.value === field.value,
                )}
              />
            </Form.Field>
          )}
        />

        {/* title */}
        <Controller
          control={control}
          name="title"
          render={({ field, fieldState }) => (
            <Form.Field
              error={fieldState.error?.message}
              label={itemLocales.title}
            >
              <Form.Input {...field} />
            </Form.Field>
          )}
        />

        {/* subtitle */}
        <Controller
          control={control}
          name="subtitle"
          render={({ field }) => (
            <Form.Field label={locales.description}>
              <Form.Textarea {...field} value={field.value ?? ''} />
            </Form.Field>
          )}
        />

        {/* fiscalId */}
        <Form.Field label={itemLocales.fiscalId}>
          <Callout>
            <Callout.Content as="div">
              <Text>
                {itemLocales.fiscalIdHelp.text}
                <Link
                  color="blue.600"
                  fontWeight="weight-400"
                  href={
                    type === InvoiceType.Invoice
                      ? INVOICE_NUMBER_ARTICLE
                      : QUOTE_NUMBER_ARTICLE
                  }
                  target="_blank"
                >
                  {itemLocales.fiscalIdHelp.link}
                </Link>
              </Text>
            </Callout.Content>
          </Callout>
        </Form.Field>

        {/* startAt */}
        <Controller
          control={control}
          name="startAt"
          render={({ field, fieldState }) => (
            <Form.Field
              error={fieldState.error?.message}
              label={locales.startAt}
            >
              <Form.SunshineInputDatePicker
                {...field}
                inputProps={{
                  rightIcon: 'calendar',
                }}
                modifiers={{
                  disabled: {
                    after: new Date(new Date().setFullYear(MAX_YEAR)),
                    before: new Date(),
                  },
                }}
                onChange={(date) => {
                  // Convert to local date to avoid timezone issues (what we
                  // really want is the date, not the time)
                  const startDate = datetimeToLocalDateUtc(date || new Date());

                  field.onChange(startDate.valueOf());
                }}
                value={new Date(field.value)}
              />
            </Form.Field>
          )}
        />
      </ClientModalProvider>
    </Stack>
  );
};

export default GeneralContent;
